import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'

import { fetchPlannerAllObservations } from 'api'
import {
  ListPagination,
  usePagedRows
} from 'app/components/generic/ListPagination'
import {
  SearchHeadCell,
  useColumnSearch
} from 'app/components/generic/SearchHeadCell'
import { showToast } from 'app/helpers'

// The server sends at most 200 a call; the page asks until it has them all so
// the heading search boxes look through every observation.
const PAGE = 200

const TYPE_LABEL = (o) => (o.type === 'preschool' ? 'Preschool' : 'Program')

// What each table column shows, for the search boxes in its heading.
const OBS_SEARCH = {
  date: (o) => o.observation_date,
  centre: (o) => o.centre_name,
  title: (o) => o.title,
  type: TYPE_LABEL,
  teacher: (o) => o.teacher_name,
  by: (o) => o.observer_name
}

// A single yes/no/blank answer, colour-coded.
const AnswerPill = ({ value }) => {
  const v = value || ''
  const label = v === 'yes' ? 'Yes' : v === 'no' ? 'No' : '—'
  const color = v === 'yes' ? '#2d9d63' : v === 'no' ? '#b3261e' : '#9a8f82'
  return (
    <Typography
      variant="body2"
      style={{ fontWeight: 600, color, minWidth: 36 }}
    >
      {label}
    </Typography>
  )
}

const QRow = ({ text, value }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    mt={0.5}
  >
    <Typography variant="body2" style={{ flex: 1, paddingRight: 12 }}>
      {text}
    </Typography>
    <AnswerPill value={value} />
  </Box>
)

const SectionHead = ({ children }) => (
  <Typography
    variant="overline"
    style={{ color: '#9a8f82', letterSpacing: 1, fontWeight: 600 }}
  >
    {children}
  </Typography>
)

// Detail for one observation — branches on type. Program observations carry
// resolved `sections`; preschool ones carry raw env_setup/facilitation keyed by
// QOT keys, which we map to their labels via the shared constants.
const ObservationDetail = ({ obs }) => {
  if (!obs) return null
  const meta = [
    obs.centre_name,
    obs.observation_date,
    obs.session_label
  ].filter(Boolean)
  return (
    <Box>
      <Typography variant="caption" color="textSecondary">
        {obs.type === 'preschool' ? 'Preschool QOT' : 'Program'} · teacher:{' '}
        {obs.teacher_name || '—'} · observed by {obs.observer_name || '—'}
      </Typography>
      {meta.length > 0 && (
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {meta.join(' · ')}
        </Typography>
      )}

      {(obs.sections || []).map((g, i) => (
        <Box key={`${g.section || 'general'}-${i}`} mt={1.5}>
          {!!g.section && <SectionHead>{g.section}</SectionHead>}
          {(g.items || []).map((it, j) => (
            <QRow key={j} text={it.text} value={it.answer} />
          ))}
        </Box>
      ))}

      {(obs.did_well || obs.to_improve || obs.comments || obs.hk_support) && (
        <Divider style={{ margin: '16px 0' }} />
      )}
      {obs.did_well && (
        <Typography variant="body2">
          <strong>Did well:</strong> {obs.did_well}
        </Typography>
      )}
      {obs.to_improve && (
        <Typography variant="body2">
          <strong>To improve:</strong> {obs.to_improve}
        </Typography>
      )}
      {obs.comments && (
        <Typography variant="body2">
          <strong>Comments:</strong> {obs.comments}
        </Typography>
      )}
      {obs.hk_support && (
        <Typography variant="body2">
          <strong>Housekeeping support:</strong> {obs.hk_support}
        </Typography>
      )}
    </Box>
  )
}

/**
 * System-wide list of every observation (preschool QOT + program), newest first.
 * Click a row to see the filled questionnaire. Backed by GET all-observations.
 */
const AllObservations = () => {
  const [rows, setRows] = useState([])
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState(null)

  const search = useColumnSearch(rows, OBS_SEARCH)
  // 25 a page, as on Life Catch-Ups (Satya, 29 Oct); a search starts again
  // on page 1.
  const { pageRows, paginationProps, resetPage } = usePagedRows(search.rows)
  useEffect(() => {
    resetPage()
  }, [search.search])

  useEffect(() => {
    let cancelled = false
    const loadAll = async () => {
      setLoading(true)
      try {
        let all = []
        let total = 0
        do {
          const d = await fetchPlannerAllObservations({
            queries: { limit: PAGE, offset: all.length }
          })
          const results = (d && d.results) || []
          total = (d && d.count) || 0
          all = [...all, ...results]
          if (!results.length) break
        } while (all.length < total)
        if (!cancelled) {
          setRows(all)
          setCount(total)
        }
      } catch (e) {
        if (!cancelled) showToast('Failed to load observations', 'error')
      }
      if (!cancelled) setLoading(false)
    }
    loadAll()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <Box mt={1}>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {/* With a heading search in use, say how many it leaves (23 Oct). */}
        {search.rows.length !== rows.length
          ? `${search.rows.length} of ${count} observations match the search.`
          : `${count} observation${
              count === 1 ? '' : 's'
            } across all centres and programmes.`}{' '}
        Click any row to see what was filled.
      </Typography>

      {loading && !rows.length ? (
        <CircularProgress />
      ) : !rows.length ? (
        <Typography variant="body2" color="textSecondary">
          No observations recorded yet.
        </Typography>
      ) : (
        // One row per observation with its parts in columns, like the
        // Newsletter list (Satya, 23 Oct).
        <Table size="small">
          <TableHead>
            <TableRow>
              <SearchHeadCell label="Date" search={search.box('date')} />
              <SearchHeadCell label="Centre" search={search.box('centre')} />
              <SearchHeadCell label="Class" search={search.box('title')} />
              <SearchHeadCell label="Type" search={search.box('type')} />
              <SearchHeadCell label="Teacher" search={search.box('teacher')} />
              <SearchHeadCell label="Observed by" search={search.box('by')} />
            </TableRow>
          </TableHead>
          <TableBody>
            {!search.rows.length && (
              <TableRow>
                <TableCell colSpan={6} style={{ textAlign: 'center' }}>
                  No observations match the search.
                </TableCell>
              </TableRow>
            )}
            {pageRows.map((o) => (
              <TableRow
                key={o.uuid}
                hover
                style={{ cursor: 'pointer' }}
                onClick={() => setSelected(o)}
              >
                <TableCell style={{ whiteSpace: 'nowrap' }}>
                  {o.observation_date || '—'}
                </TableCell>
                <TableCell>{o.centre_name || '—'}</TableCell>
                <TableCell style={{ fontWeight: 600 }}>{o.title}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={TYPE_LABEL(o)}
                    style={{
                      backgroundColor:
                        o.type === 'preschool' ? '#eef4ff' : '#efe9df',
                      color: o.type === 'preschool' ? '#2a5db0' : '#7a6f65',
                      fontWeight: 600
                    }}
                  />
                </TableCell>
                <TableCell>{o.teacher_name || '—'}</TableCell>
                <TableCell>{o.observer_name || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <ListPagination {...paginationProps} />

      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle>{selected ? selected.title : 'Observation'}</DialogTitle>
        <DialogContent dividers>
          <ObservationDetail obs={selected} />
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default AllObservations
